import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminModuleId, ADMIN_MODULE_ORDER } from './services/firebaseFunctions';
import LoginPage from './pages/LoginPage';
import CompaniesPage from './pages/CompaniesPage';
import CompanyListsPage from './pages/CompanyListsPage';
import TitlesPage from './pages/TitlesPage';
import PublishersPage from './pages/PublishersPage';
import PublisherDetailPage from './pages/PublisherDetailPage';
import PublisherGrantsReportPage from './pages/PublisherGrantsReportPage';
import ListsPage from './pages/ListsPage';
import UploadPage from './pages/UploadPage';
import UsersPage from './pages/UsersPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import EvoWebhooksPage from './pages/EvoWebhooksPage';
import EvoWebhooksBackfillPage from './pages/EvoWebhooksBackfillPage';
import EvoWebhookDetailPage from './pages/EvoWebhookDetailPage';
import EvoWebhookConferencePage from './pages/EvoWebhookConferencePage';
import EvoConferencePage from './pages/EvoConferencePage';
import ProductsPage from './pages/ProductsPage';
import CombosPage from './pages/CombosPage';
import AdminNotificationsPage from './pages/AdminNotificationsPage';
import DeliveryQueuePage from './pages/DeliveryQueuePage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import NetworksPage from './pages/NetworksPage';
import NetworkDetailPage from './pages/NetworkDetailPage';
import CompanyGroupsPage from './pages/CompanyGroupsPage';
import CompanyGroupDetailPage from './pages/CompanyGroupDetailPage';
import DeliveriesReportPage from './pages/DeliveriesReportPage';
import AdminsPage from './pages/AdminsPage';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

/**
 * Rota de destino de cada módulo do menu — usada só pela landing (`/`) para decidir para onde
 * navegar (risco R18: a landing deixou de ser fixa em `/companies`). Mantida ao lado de
 * `ADMIN_MODULE_ORDER` (mesma ordem do menu em `Layout.tsx`), não dentro do serviço, porque é
 * detalhe de roteamento do `reedy-admin`, não do contrato cross-project.
 */
const ROUTE_BY_MODULE: Record<AdminModuleId, string> = {
  analytics: '/analytics',
  empresas: '/companies',
  redes: '/networks',
  titulos: '/titles',
  editoras: '/publishers',
  listas: '/lists',
  ativos: '/products',
  combos: '/combos',
  upload: '/upload',
  usuarios: '/users',
  webhooks_evo: '/evo-webhooks',
  conferencia_evo: '/evo-conference',
  notificacoes: '/admin-notifications',
  entregas_pendentes: '/delivery-queue',
  relatorios: '/reports',
  administradores: '/admins',
};

/**
 * Landing (`/`) — antes fixa em `/companies` (risco R18: mandava para "Acesso Negado" qualquer
 * administrador sem permissão em `empresas`). Agora navega para o primeiro módulo permitido do
 * administrador logado. Só é alcançada depois que `ProtectedRoute` já confirmou "administrador
 * ativo" — se `adminAccess` ainda não chegou aqui por algum motivo, não navega para lugar nenhum
 * (evita loop), apenas nada é renderizado até o próximo estado do `AuthContext`.
 */
const LandingRedirect: React.FC = () => {
  const { adminAccess, hasPermission } = useAuth();

  if (!adminAccess) return null;

  const firstPermittedModule = ADMIN_MODULE_ORDER.find((moduleId) => hasPermission(moduleId, 'ver'));

  if (!firstPermittedModule) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="60vh" gap={2} p={3}>
        <Typography variant="h5" gutterBottom>
          Nenhum módulo disponível
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Seu perfil de administrador não tem permissão de visualização em nenhum módulo. Peça a
          um administrador com acesso a "Administradores" para revisar suas permissões.
        </Typography>
      </Box>
    );
  }

  return <Navigate to={ROUTE_BY_MODULE[firstPermittedModule]} replace />;
};

// Componente principal da aplicação
const AppContent: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <LandingRedirect />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute module="empresas">
            <Layout>
              <CompaniesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/networks" element={<ProtectedRoute module="redes"><Layout><NetworksPage /></Layout></ProtectedRoute>} />
      <Route path="/networks/:id" element={<ProtectedRoute module="redes"><Layout><NetworkDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/company-groups" element={<ProtectedRoute module="empresas"><Layout><CompanyGroupsPage /></Layout></ProtectedRoute>} />
      <Route path="/company-groups/:id" element={<ProtectedRoute module="empresas"><Layout><CompanyGroupDetailPage /></Layout></ProtectedRoute>} />
      <Route
        path="/companies/:id/lists"
        element={
          <ProtectedRoute module="empresas">
            <Layout>
              <CompanyListsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/titles"
        element={
          <ProtectedRoute module="titulos">
            <Layout>
              <TitlesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/publishers"
        element={
          <ProtectedRoute module="editoras">
            <Layout>
              <PublishersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/publishers/:id/relatório"
        element={
          <ProtectedRoute module="editoras">
            <Layout>
              <PublisherGrantsReportPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/publishers/:id"
        element={
          <ProtectedRoute module="editoras">
            <Layout>
              <PublisherDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/lists"
        element={
          <ProtectedRoute module="listas">
            <Layout>
              <ListsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/upload"
        element={
          <ProtectedRoute module="upload">
            <Layout>
              <UploadPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute module="usuarios">
            <Layout>
              <UsersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evo-webhooks"
        element={
          <ProtectedRoute module="webhooks_evo">
            <Layout>
              <EvoWebhooksPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evo-webhooks/:id/conference"
        element={
          <ProtectedRoute module="webhooks_evo">
            <Layout>
              <EvoWebhookConferencePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evo-webhooks/backfill"
        element={
          <ProtectedRoute module="webhooks_evo">
            <Layout>
              <EvoWebhooksBackfillPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evo-webhooks/:id"
        element={
          <ProtectedRoute module="webhooks_evo">
            <Layout>
              <EvoWebhookDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/evo-conference"
        element={
          <ProtectedRoute module="conferencia_evo">
            <Layout>
              <EvoConferencePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute module="ativos">
            <Layout>
              <ProductsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/combos"
        element={
          <ProtectedRoute module="combos">
            <Layout>
              <CombosPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-notifications"
        element={
          <ProtectedRoute module="notificacoes">
            <Layout>
              <AdminNotificationsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/delivery-queue"
        element={
          <ProtectedRoute module="entregas_pendentes">
            <Layout>
              <DeliveryQueuePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute module="analytics">
            <Layout>
              <AnalyticsDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute module="relatorios">
            <Layout>
              <DeliveriesReportPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admins"
        element={
          <ProtectedRoute module="administradores">
            <Layout>
              <AdminsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

// Componente App principal
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
